import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subscription, of, timer } from 'rxjs';
import { catchError, exhaustMap, finalize, map, shareReplay } from 'rxjs/operators';
import { PayarenaService } from './payarena.service';
import { UtilsService } from './utils.service';

/** Refreshes the identity session before its 900-second lifetime ends. */
@Injectable({
  providedIn: 'root',
})
export class TokenRefreshService implements OnDestroy {
  private readonly refreshEveryMs = 700 * 1000;
  private schedule?: Subscription;
  private refreshInProgress: Observable<string | null> | null = null;

  constructor(
    private payarena: PayarenaService,
    private utils: UtilsService,
  ) { }

  /** Starts a silent refresh every 700 seconds for the current signed-in user. */
  start(): void {
    if (this.schedule) return;
    // Refresh immediately for an existing session, then before each 900-second
    // token expiry (every 700 seconds).
    this.schedule = timer(0, this.refreshEveryMs).pipe(
      exhaustMap(() => this.refreshAccessToken()),
    ).subscribe();
  }

  /** Restarts the 700-second countdown when a new session is received. */
  restart(): void {
    this.stop();
    this.start();
  }

  stop(): void {
    this.schedule?.unsubscribe();
    this.schedule = undefined;
  }

  /**
   * Refreshes once and shares the request with all callers. This prevents two
   * consumers (the timer and a 401 retry) rotating the same refresh token.
   */
  refreshAccessToken(): Observable<string | null> {
    if (this.refreshInProgress) return this.refreshInProgress;

    const refreshToken = localStorage.getItem(this.utils.STORAGE_KEYS.REFRESH_TOKEN);
    if (!refreshToken) return of(null);

    this.refreshInProgress = this.payarena.refreshAccessToken(refreshToken).pipe(
      map((response) => {
        // Do not replace a valid session with a partial response.
        if (!response?.identity_id || !response.identity_token || !response.refresh_token) {
          return null;
        }

        localStorage.setItem(this.utils.STORAGE_KEYS.TOKEN, response.identity_token);
        localStorage.setItem(this.utils.STORAGE_KEYS.IDENTITY_ID, response.identity_id);
        localStorage.setItem(this.utils.STORAGE_KEYS.IDENTITY_TOKEN, response.identity_token);
        localStorage.setItem(this.utils.STORAGE_KEYS.MALL_TOKEN, response.identity_token);
        localStorage.setItem(this.utils.STORAGE_KEYS.REFRESH_TOKEN, response.refresh_token);
        return response.identity_token;
      }),
      // Periodic refresh is intentionally silent. A later protected request can
      // still use its 401 recovery path or the native Flutter token provider.
      catchError(() => of(null)),
      finalize(() => (this.refreshInProgress = null)),
      shareReplay(1),
    );

    return this.refreshInProgress;
  }

  ngOnDestroy(): void {
    this.stop();
  }
}
